"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "wouter";
import { cn } from "@/lib/utils";

interface NavigationTransitionsProps {
  children: React.ReactNode;
  className?: string;
  mode?: "fade" | "slide" | "slide-up" | "scale" | "none";
}

const transitionVariants = {
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.2 },
  },
  slide: {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -20 },
    transition: { type: "spring", stiffness: 300, damping: 30 },
  },
  "slide-up": {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 },
    transition: { type: "spring", stiffness: 300, damping: 30 },
  },
  scale: {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 1.02 },
    transition: { type: "spring", stiffness: 300, damping: 30 },
  },
  none: {
    initial: {},
    animate: {},
    exit: {},
    transition: { duration: 0 },
  },
};

export function NavigationTransitions({
  children,
  className,
  mode = "slide-up",
}: NavigationTransitionsProps) {
  const [location] = useLocation();
  const variant = transitionVariants[mode];

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location}
        initial={variant.initial}
        animate={variant.animate}
        exit={variant.exit}
        transition={variant.transition}
        className={cn("min-h-screen", className)}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

// Optimized page transition wrapper for mobile
export function MobilePageTransition({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{
        type: "spring",
        stiffness: 380,
        damping: 30,
        mass: 0.8,
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Staggered children animation for lists
export function StaggeredList({
  children,
  className,
  staggerDelay = 0.05,
}: {
  children: React.ReactNode;
  className?: string;
  staggerDelay?: number;
}) {
  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{
        visible: {
          transition: {
            staggerChildren: staggerDelay,
          },
        },
      }}
      className={className}
    >
      {React.Children.map(children, (child, index) => (
        <motion.div
          key={index}
          variants={{
            hidden: { opacity: 0, y: 10 },
            visible: { opacity: 1, y: 0 },
          }}
          transition={{
            type: "spring",
            stiffness: 300,
            damping: 25,
          }}
        >
          {child}
        </motion.div>
      ))}
    </motion.div>
  );
}

// Shared element transition hook
export function useSharedElementTransition(elementId: string) {
  const [bounds, setBounds] = React.useState<DOMRect | null>(null);

  const registerElement = React.useCallback(() => {
    const element = document.getElementById(elementId);
    if (element) {
      setBounds(element.getBoundingClientRect());
    }
  }, [elementId]);

  React.useEffect(() => {
    registerElement();
  }, [registerElement]);

  return { bounds, registerElement };
}

export default NavigationTransitions;
